import { useState, useEffect, Fragment  } from 'react';
import { DataTable } from 'mantine-datatable'
import { Dialog, Transition } from '@headlessui/react';
import api from '../../api/api';
import Loader from '../../component/layout/Loader';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const CheckInHistory = () => {
    const [loading, setLoading] = useState(false)
    const [items, setItems] = useState([])
    const [initialRecords, setInitialRecords] = useState<any[]>(items)
    const [records, setRecords] = useState(initialRecords)
    const [page, setPage] = useState(1)
    const [totalRecords, setTotalRecords] = useState(0)
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [detailData, setDetailData] = useState<any>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const { t } = useTranslation()

     
    useEffect(() => {
        getHistoryList(page, 10)
    }, [page])

    const getHistoryList = async (currentPage: number = page, currentPageSize: number = 10) => {
        setLoading(true)
        try {
            const response = await api.get('/coach/clock', {
                params: {
                    page: currentPage,
                    page_size: currentPageSize
                }
            })
            // console.log('Clock History Response:', response)
            const data = response?.data?.data || []
            const total = response?.data?.total || response?.data?.count || data.length
            
            setInitialRecords(data)
            setRecords(data)
            setTotalRecords(total)
        } catch (error: any) {
            toast.error(t('server_error') || 'Error fetching clock history')
            setInitialRecords([])
            setRecords([])
            setTotalRecords(0)
        } finally {
            setLoading(false)
        }
    };

    const handlePrevPage = () => {
        if (page > 1) {
            setPage(page - 1)
        }
    }

    const handleNextPage = () => {
        setPage(page + 1)
    }

    const getDetailData = async (id: string) => {
        setDetailLoading(true)
        try {
            const response = await api.get(`/coach/clock/view/${id}`)
            // console.log('Detail Response:', response)
            const data = response?.data?.data || response?.data
            setDetailData(data)
            setShowDetailModal(true)
        } catch (error: any) {
            toast.error(t('server_error') || 'Error fetching detail')
            console.error('Detail fetch error:', error)
        } finally {
            setDetailLoading(false)
        }
    };

    const handleRowClick = (record: any) => {
        if (record.record?.id) {
            getDetailData(record.record.id)
        }
    };

    const formatTime = (timeString: string) => {
        return new Date(timeString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        })
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        })
    };


    return (
    <div>
        {loading && (<Loader/>)}
        <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-xl">{t('checkin.checkin_history')}</h2>
        </div>

        <div className="mt-5 panel p-0 border-0 overflow-hidden">
            <div className="table-responsive">
                <DataTable
                    className="whitespace-nowrap table-hover invoice-table cursor-pointer"
                    records={records}
                    onRowClick={(record) => handleRowClick(record)}
                    columns={[
                        {
                            accessor: 'type',
                            title: t("checkin.status"),
                            render: ({ type }) => (
                                <div className={`inline-block px-3 py-1 rounded-full text-center text-sm font-medium w-24
                                    ${type.toLowerCase() === "in" 
                                        ? "bg-[#E8FFF1] text-primary" 
                                        : "bg-[#FFE8E8] text-red-600"}`
                                }>
                                    {type === "in" ? "Check In" : "Check Out"}
                                </div>
                            ),
                        },
                        {
                            accessor: 'branch_name',
                            title: t("checkin.branch_name"),
                            titleClassName: "Branch Name",
                            render: ({ branch_name }) => (
                                <div>
                                    <div className="text-black font-semibold truncate max-w-[200px]" title={branch_name}>
                                        {branch_name}
                                    </div>
                                </div>
                            ),
                        },
                        {
                            accessor: 'time',
                            title: t("checkin.time"),
                            titleClassName: "Time",
                            render: ({ type, check_in_time, check_out_time }) => (
                                <div>
                                    <div className="text-black font-semibold">
                                        <div>{formatDate(check_in_time)}</div>
                                        <div>
                                            {type === "in" 
                                                ? formatTime(check_in_time)
                                                : `${formatTime(check_in_time)} - ${formatTime(check_out_time)}`
                                            }
                                        </div>
                                    </div>
                                </div>
                            ),
                        },
                    ]}
                    // totalRecords={totalRecords}
                    // recordsPerPage={pageSize}
                    // page={page}
                    // onPageChange={(p) => setPage(p)}
                    // recordsPerPageOptions={PAGE_SIZES}
                    // onRecordsPerPageChange={setPageSize}
                    minHeight={200}
                    fetching={loading}
                />
            </div>
        </div>


        <div className="relative inline-flex align-middle float-end mt-3">
            <button 
                type="button" 
                className="btn btn-primary ltr:rounded-r-none rtl:rounded-l-none" 
                onClick={handlePrevPage}
                disabled={page <= 1}
            >
                {"<"}
            </button>
            {/* <span className="btn btn-primary bg-white text-primary border-primary px-4">
                {page}
            </span> */}
            <button 
                type="button" 
                className="btn btn-primary ltr:rounded-l-none rtl:rounded-r-none" 
                onClick={handleNextPage}
                // disabled={records.length < pageSize}
            >
                {">"}
            </button>
        </div>

        {/* Detail Modal */}
        <Transition appear show={showDetailModal} as={Fragment}>
            <Dialog as="div" open={showDetailModal} onClose={() => setShowDetailModal(false)} className="relative z-[51]">
                <Transition.Child 
                    as={Fragment} 
                    enter="ease-out duration-300" 
                    enterFrom="opacity-0" 
                    enterTo="opacity-100" 
                    leave="ease-in duration-200" 
                    leaveFrom="opacity-100" 
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-[black]/60" />
                </Transition.Child>
                
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center px-4 py-8">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                                {detailLoading ? (
                                    <div className="p-6 text-center">
                                        <Loader />
                                    </div>
                                ) : detailData ? (
                                    <div className="relative">
                                        {/* Header with green background */}
                                        <div className="bg-primary text-white p-3 py-4 rounded-t-2xl relative">
                                            {/* <button
                                                type="button"
                                                className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                                                onClick={() => setShowDetailModal(false)}
                                            >
                                                <IconX className="w-4 h-4" />
                                            </button> */}
                                            
                                            <div className="text-center">
                                                <h2 className="text-xl font-bold mb-4 truncate px-4" title={detailData.branch_name}>
                                                    {detailData.branch_name}
                                                </h2>
                                                
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="bg-white/20 rounded-lg px-4 py-2">
                                                        <span className="text-sm">{formatDate(detailData.date)}</span>
                                                    </div>
                                                    <div className="bg-white/20 rounded-lg px-4 py-2">
                                                        <span className="text-sm truncate">
                                                            {detailData.check_out_time 
                                                                ? `${formatTime(detailData.check_in_time)} - ${formatTime(detailData.check_out_time)}`
                                                                : formatTime(detailData.check_in_time)
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Image section */}
                                        <div className="p-0">
                                            {detailData.picture ? (
                                                <img 
                                                    src={detailData.picture} 
                                                    alt="Check-in location" 
                                                    className="w-full h-64 object-cover"
                                                    onError={(e) => {
                                                        e.currentTarget.src = '/assets/images/no-image-placeholder.jpg'
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                                                    <span className="text-gray-500">No image available</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-6 text-center">
                                        <span className="text-gray-500">No data available</span>
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>

    </div>
    )
}

export default CheckInHistory